import type { GroupUserRole } from '@/types/groups';

/** Select options for group member roles (Reader first as default). */
export const GROUP_USER_ROLE_OPTIONS: Array<{ value: GroupUserRole; label: string }> = [
  { value: 'READER', label: 'Reader' },
  { value: 'EDITOR', label: 'Editor' },
  { value: 'ADMIN', label: 'Admin' },
];

const ROLE_LABELS: Record<GroupUserRole, string> = {
  ADMIN: 'Admin',
  EDITOR: 'Editor',
  READER: 'Reader',
};

const ROLE_SORT_ORDER: Record<GroupUserRole, number> = {
  ADMIN: 0,
  EDITOR: 1,
  READER: 2,
};

/**
 * Human-readable label for a group user role.
 */
export function getGroupRoleLabel(role: GroupUserRole): string {
  return ROLE_LABELS[role];
}

/**
 * Resolves the effective group role from API fields.
 */
export function resolveGroupUserRole(member: {
  isAdmin: boolean;
  userRole?: GroupUserRole | string | null;
}): GroupUserRole {
  if (member.userRole) {
    const normalized = String(member.userRole).toUpperCase() as GroupUserRole;
    if (normalized === 'ADMIN' || normalized === 'EDITOR' || normalized === 'READER') {
      return normalized;
    }
  }
  return member.isAdmin ? 'ADMIN' : 'READER';
}

/**
 * Badge variant for a group user role.
 */
export function getGroupRoleBadgeVariant(
  role: GroupUserRole,
): 'default' | 'secondary' | 'outline' {
  if (role === 'ADMIN') return 'default';
  if (role === 'EDITOR') return 'secondary';
  return 'outline';
}

/**
 * Sort priority for group member lists (admin first, then editor, then reader).
 */
export function getGroupRoleSortOrder(role: GroupUserRole): number {
  return ROLE_SORT_ORDER[role];
}
